import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAppState } from '../../../Context/AppStateContext';
import { useSanityData } from '../../../Context/SanityDataContext';
import { useSanityLocalized } from '../../../hooks/useSanityLocalized';
import { theme } from '../../../theme';
import BorderBox from '../../ui/BorderBox.component';
import Modal from '../../ui/Modal.component';
import MotionContainer from '../../ui/MotionContainer.component';
import ScrollableContent from '../../ui/ScrollableContent.component';
import TabSelector from '../../ui/TabSelector.component';
import Timeline, {
  TimelineItem,
  ItemHeader,
  ItemTitle,
  ItemPeriod,
  BadgeContainer,
  Badge,
  ItemDescription,
} from '../../ui/Timeline.component';

interface CareerProps {
  visible: boolean;
  onClose: () => void;
  onFocus?: () => void;
  isActive?: boolean;
}

const CareerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  overflow: hidden;
`;

const CertificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const CertificationCard = styled.div`
  background: rgba(31, 207, 94, 0.05);
  border: 1px solid rgba(31, 207, 94, 0.3);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.green};
    box-shadow: 0 0 15px rgba(31, 207, 94, 0.2);
  }
`;

const CertHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const CertTitle = styled.h4`
  margin: 0;
  color: ${theme.colors.green};
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    font-size: 18px;
    color: ${theme.colors.green};
  }
`;

const CertIssuer = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
`;

const CertDate = styled.span`
  background: rgba(31, 207, 94, 0.1);
  color: ${theme.colors.green};
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-family: 'Courier New', monospace;
`;

const CertDescription = styled.p`
  margin: 8px 0 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  line-height: 1.5;
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.green};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(31, 207, 94, 0.3);

  svg {
    font-size: 22px;
  }
`;

const LoadingText = styled.div`
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  padding: 20px;
`;

const Career: React.FC<CareerProps> = ({ visible, onClose, onFocus, isActive }) => {
  const { t } = useTranslation();
  const { isOnMobile } = useAppState();
  const { workExperience, certifications, isLoading } = useSanityData();
  const { getLocalizedValue, getLocalizedText } = useSanityLocalized();
  const [activeTab, setActiveTab] = useState<'experience' | 'certifications'>('experience');

  const handleTabChange = (id: string | number) => {
    setActiveTab(id as 'experience' | 'certifications');
  };

  if (!visible) return null;

  const tabItems = [
    {
      id: 'experience',
      label: t('modals.career.tabs.experience'),
      icon: <WorkHistoryIcon fontSize="small" />,
    },
    {
      id: 'certifications',
      label: t('modals.career.tabs.certifications'),
      icon: <VerifiedIcon fontSize="small" />,
    },
  ];

  return (
    <Modal
      color={theme.colors.green}
      header={t('modals.career.header')}
      widthString={isOnMobile ? '90%' : '800px'}
      xPositionPercent={isOnMobile ? 50 : 45}
      yPositionPercent={20}
      onClose={onClose}
      onFocus={onFocus}
      isActive={isActive}
      icon={<WorkHistoryIcon />}
    >
      <CareerContainer>
        <TabSelector items={tabItems} activeId={activeTab} onTabChange={handleTabChange} />

        <ScrollableContent>
          {isLoading ? (
            <LoadingText>&gt; Loading career data from Sanity...</LoadingText>
          ) : (
            <AnimatePresence mode="wait">
              {activeTab === 'experience' ? (
                <MotionContainer animationKey="experience">
                  <BorderBox isActive={true} noBackground={true}>
                    <SectionTitle>
                      <BusinessIcon /> {t('modals.career.sections.workHistory')}
                    </SectionTitle>
                    <Timeline
                      items={workExperience.map((work) => ({
                        id: work._id,
                        title: work.company,
                        period: work.period,
                        badges: [getLocalizedValue(work.role)],
                        description: getLocalizedText(work.description),
                      }))}
                      renderItem={(item) => (
                        <TimelineItem key={item.id}>
                          <ItemHeader>
                            <div>
                              <ItemTitle>
                                <BusinessIcon fontSize="small" /> {item.title}
                              </ItemTitle>
                              {item.badges && item.badges.length > 0 && (
                                <BadgeContainer>
                                  {item.badges.map((badge, idx) => (
                                    <Badge key={idx}>{badge}</Badge>
                                  ))}
                                </BadgeContainer>
                              )}
                            </div>
                            <ItemPeriod>{item.period}</ItemPeriod>
                          </ItemHeader>
                          {item.description && (
                            <ItemDescription>{item.description}</ItemDescription>
                          )}
                        </TimelineItem>
                      )}
                    />
                  </BorderBox>
                </MotionContainer>
              ) : (
                <MotionContainer animationKey="certifications">
                  <BorderBox isActive={true} noBackground={true}>
                    <SectionTitle>
                      <SchoolIcon /> {t('modals.career.sections.certifications')}
                    </SectionTitle>
                    <CertificationsGrid>
                      {certifications.map((cert) => (
                        <CertificationCard key={cert._id}>
                          <CertHeader>
                            <div>
                              <CertTitle>
                                <VerifiedIcon /> {cert.name}
                              </CertTitle>
                              <CertIssuer>{cert.issuer}</CertIssuer>
                            </div>
                            <CertDate>{cert.date}</CertDate>
                          </CertHeader>
                          <CertDescription>
                            {getLocalizedText(cert.description)}
                          </CertDescription>
                        </CertificationCard>
                      ))}
                    </CertificationsGrid>
                  </BorderBox>
                </MotionContainer>
              )}
            </AnimatePresence>
          )}
        </ScrollableContent>
      </CareerContainer>
    </Modal>
  );
};

export default Career;
